'use client'

import { createUrl } from 'lib/utils';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  type RefinementFunction
} from 'propel-react-components';
import { useCallback } from 'react';

/**
 * Hook returns a function that performs a search refinement
 */
export const useRefinementFunction = (): RefinementFunction => {
  const router = useRouter();
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();

  const refinementFunction: RefinementFunction = useCallback(
    async ({ type, field, value, low, high, multiple, checked, clear_all, view_all }) => {
      // Create a function callback to update newParams
      const updateNewParams = (callback: (params: URLSearchParams) => void) => {
        const newParamsCopy = new URLSearchParams(searchParams?.toString());
        callback(newParamsCopy);

        router.push(createUrl(pathname, newParamsCopy), { scroll: false });
      };

      if (clear_all) {
        updateNewParams((params) => {
          const filterParamsToDelete: string[] = [];
          // Identify filter parameters to delete
          params.forEach((value, key) => {
            if (key.startsWith('filter.')) {
              filterParamsToDelete.push(key);
            }
          });

          // Delete filter parameters
          filterParamsToDelete.forEach((key) => {
            params.delete(key);
          });
        })
      }

      if (view_all) {
        updateNewParams((params) => {
          const categoryHierarchyParamsToDelete: string[] = [];

          // Identify category_hierarchy parameters to delete
          params.forEach((value, key) => {
            if (key.startsWith('filter.')) {
              categoryHierarchyParamsToDelete.push(key);
            }
          });

          // Delete category_hierarchy parameters
          categoryHierarchyParamsToDelete.forEach((key) => {
            params.delete(key);
          });
        });
      }

      switch (type) {
        // TODO: currently missing handling for palette and hierarchy options.
        case 'slider':
          updateNewParams((params) => {
            if (low || low === 0) {
              params.set(`filter.${field}.low`, String(low));
            }
            if (high) {
              params.set(`filter.${field}.high`, String(high));
            }
          });
          break;
        default:
          if (value || low || high) {
            switch (multiple) {
              case 'single':
                updateNewParams((params) => {
                  if (checked) {
                    params.set(`filter.${field}`, String(value));
                  } else {
                    params.delete(`filter.${field}`, String(value));
                  }
                });
                break;
              default:
                updateNewParams((params) => {
                  if (value !== undefined) {
                    const existingValues = params.getAll(`filter.${field}`);
                    if (checked) {
                      // Add the value if checked
                      if (!existingValues.includes(String(value))) {
                        params.append(`filter.${field}`, String(value));
                      }
                    } else {
                      // Remove the value if unchecked
                      params.delete(`filter.${field}`);
                      existingValues.forEach((val) => {
                        if (val !== String(value)) {
                          params.append(`filter.${field}`, val);
                        }
                      });
                    }
                  }

                  // Remove applied filter range handle
                  if (field === 'calculated_price' && typeof value === 'string') {
                    let parsedValues: string | any[] = [];
                    let isLowOpenEnded = false;
                    let isHighOpenEnded = false;
                    if (value.includes('to')) {
                      parsedValues = value.match(/\d+/g)?.map(Number) || [];
                    } else if (value.includes('More than')) {
                      const match = value.match(/\d+/);
                      if (match) {
                        parsedValues = [parseInt(match[0]), Infinity];
                        isHighOpenEnded = true;
                      }
                    } else if (value.includes('Less than')) {
                      const match = value.match(/\d+/);
                      if (match) {
                        parsedValues = [-Infinity, parseInt(match[0])];
                        isLowOpenEnded = true;
                      }
                    }

                    if (parsedValues.length > 0) {
                      const [parsedLow, parsedHigh] = parsedValues;
                      ['low', 'high'].forEach((suffix) => {
                        params.getAll(`filter.${field}.${suffix}`).forEach((paramValue, index) => {
                          if ((suffix === 'low' && (isLowOpenEnded ? paramValue === '*' : parseInt(paramValue) === parsedLow)) ||
                            (suffix === 'high' && (isHighOpenEnded ? paramValue === '*' : parseInt(paramValue) === parsedHigh))) {
                            const values = params.getAll(`filter.${field}.${suffix}`);
                            values.splice(index, 1);
                            params.delete(`filter.${field}.${suffix}`);
                            values.forEach(v => params.append(`filter.${field}.${suffix}`, v));
                          }
                        });
                      });
                    }
                    return;
                  }

                  if (low !== undefined && high !== undefined) {
                    const entries = Array.from(params.entries());
                    let lowFound = false, highFound = false;
                    let lowKeyToRemove: number | null = null, highKeyToRemove: number | null = null;

                    entries.forEach(([key, value], index) => {
                      if (key === `filter.${field}.low` && value === String(low)) {
                        lowFound = true;
                        lowKeyToRemove = index;
                      }
                      if (key === `filter.${field}.high` && value === String(high)) {
                        highFound = true;
                        highKeyToRemove = index;
                      }
                    });

                    if (lowFound && highFound && lowKeyToRemove !== null && highKeyToRemove !== null) {
                      const newParams = new URLSearchParams();
                      entries.forEach(([key, value], index) => {
                        if (!key.startsWith('filter')) return;
                        if (index !== lowKeyToRemove && index !== highKeyToRemove) {
                          newParams.append(key, value);
                        }
                      });
                      params.delete(`filter.${field}.low`);
                      params.delete(`filter.${field}.high`);
                      newParams.forEach((value, key) => {
                        params.append(key, value);
                      });
                      params.delete('view');
                    } else {
                      params.append(`filter.${field}.low`, String(low));
                      params.append(`filter.${field}.high`, String(high));
                    }
                  }
                }
                );
            }
          }
      }
    },
    [searchParams, pathname, router],
  );

  (refinementFunction as any).displayName = 'RefinementFunction';

  return refinementFunction;
};
